// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";

import { removeNullAndUndefinedProps } from "@transformers/object";
import actionCreator from "@modules/service/actionCreator";
import * as selectors from "@modules/service/selectors";
import { Button, View } from "@components";
import { RtwFormForm } from "@modules/service/components";
import type { Rtw, RtwFormQuestion } from "@modules/service/reducer/rtw/types";
import type { StepProps } from "@components";
import schema from "./form-questions-schema";

type StepData = $Exact<{
  formQuestions: Array<RtwFormQuestion>,
}>;

type OwnProps = $Exact<{
  editing: boolean,
}>;

type PropsFromState = $Exact<{
  schema: ?Object,
  draft: ?Rtw,
}>;

type PropsFromDispatch = $Exact<{
  setRtwDraft: typeof actionCreator.SetRtwDraft,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

export class StepAbsenceReasons extends React.Component<Props> {
  validate: *;

  componentDidMount() {
    if (this.props.schema) {
      this.setValidators();
    }
  }

  componentDidupdate(prevProps: Props) {
    if (this.props.schema && this.props.schema !== prevProps.schema) {
      this.setValidators();
    }
  }

  setValidators = () => {
    this.validate = new Ajv().compile(this.schema);
  };

  get data(): StepData {
    return { formQuestions: (this.props.draft?.content || {}).formQuestions || [] };
  }

  get schema() {
    return schema;
  }

  isValid = (data: Object) => {
    if (!this.validate) {
      return false;
    }

    const cleanData = (this.data.formQuestions || []).map(d => removeNullAndUndefinedProps(d));

    const isValid = this.validate(cleanData);
    if (process.env.NODE_ENV !== "production" && !isValid) {
      console.warn("isValid errors", this.validate.errors);
    }
    return isValid;
  };

  handleSubmit = () => {
    if (this.props.handleSubmit) {
      this.props.handleSubmit({}, true);
    }
  };

  handleChange = (data: Object) => {
    this.props.setRtwDraft({
      ...this.props.draft,
      content: {
        ...this.props.draft?.content,
        formQuestions: data,
      },
    });
  };

  render() {
    if (!this.props.editable) {
      return <div />;
    }

    return (
      <React.Fragment>
        <View flex={1} gap={4}>
          {this.schema ? (
            <View className="gs-container">
              <RtwFormForm
                data={this.data.formQuestions}
                schema={this.schema.items}
                onChange={this.handleChange}
              />
            </View>
          ) : null}
        </View>
        {this.props.editing ? null : (
          <div className="w-100 d-flex justify-content-end mt-5 mb-3">
            <Button
              materialIcon="fast_forward"
              onClick={this.handleSubmit}
              disabled={!this.isValid(this.data)}
            >
              {this.props.t("NEXT")}
            </Button>
          </div>
        )}
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    schema: selectors.getRtwSchema(state),
    draft: selectors.getRtwDraft(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setRtwDraft: actionCreator.SetRtwDraft,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(StepAbsenceReasons);
